import React, { useState, useEffect } from 'react';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';

interface Institute {
  institute_id: string;
  name: string;
  address: string;
  contact_no: string;
  email: string;
  created_at: string;
}

interface ApiResponse {
  success: boolean;
  data: Institute[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    pages: number;
  };
}

// ── HCI brand tokens (from the logo) ──────────────────────────────────────────
const C = {
  navy:     '#14267A',
  navyDeep: '#0B1650',
  gold:     '#F5B326',
  tint:     '#E8EBF8',
  tintSoft: '#F4F5FA',
  line:     '#E2E5F2',
  lineStrong: '#DADFF0',
  text:     '#12183A',
  textSoft: '#5A6080',
};

const InstituteProfile: React.FC = () => {
  const [institute, setInstitute] = useState<Institute | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetchInstitute();
  }, []);

  const fetchInstitute = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiClient.get<ApiResponse>('/v1/institutes');

      if (response.data.success && response.data.data.length > 0) {
        setInstitute(response.data.data[0]);
      } else {
        setError('No institute found for this account');
      }
    } catch (err: any) {
      console.error('Failed to fetch institute:', err);
      setError(err.response?.data?.error || 'Failed to load institute details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const copyId = async () => {
    if (!institute) return;
    try {
      await navigator.clipboard.writeText(institute.institute_id);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard not available – ignore */
    }
  };

  const wrap: React.CSSProperties = { fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", color: C.text };

  if (loading) {
    return (
      <div style={{ ...wrap, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '360px', background: C.tintSoft, borderRadius: '18px' }}>
        <div style={{ textAlign: 'center' }}>
          <ProgressSpinner style={{ width: '44px', height: '44px' }} strokeWidth="3" />
          <p style={{ marginTop: '16px', color: C.navy, fontSize: '0.85rem', fontWeight: 600 }}>
            Loading institute details...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ ...wrap, padding: '32px', background: C.tintSoft, borderRadius: '18px' }}>
        <Message severity="error" text={error} style={{ width: '100%', justifyContent: 'center', marginBottom: '16px' }} />
        <div style={{ textAlign: 'center' }}>
          <Button label="Retry" icon="pi pi-refresh" onClick={fetchInstitute} severity="danger" outlined />
        </div>
      </div>
    );
  }

  if (!institute) {
    return (
      <div style={{ ...wrap, padding: '32px', background: C.tintSoft, borderRadius: '18px', textAlign: 'center' }}>
        <Message severity="warn" text="No institute information available" style={{ width: '100%', justifyContent: 'center' }} />
      </div>
    );
  }

  // Small reusable info tile
  const InfoTile = ({ icon, label, value, breakWord = false }: { icon: string; label: string; value: string; breakWord?: boolean }) => (
    <div
      style={{
        background: 'white', border: `1px solid ${C.line}`, borderRadius: '14px', padding: '12px',
        display: 'flex', alignItems: 'flex-start', gap: '10px',
        transition: 'background-color 0.25s ease, border-color 0.25s ease',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = C.tintSoft; e.currentTarget.style.borderColor = '#C6CDE8'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = 'white'; e.currentTarget.style.borderColor = C.line; }}
    >
      <div style={{ width: '30px', height: '30px', borderRadius: '9px', background: C.tint, color: C.navy, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <i className={`pi ${icon}`} style={{ fontSize: '0.8rem' }}></i>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '0.585rem', fontWeight: 700, letterSpacing: '0.6px', textTransform: 'uppercase', color: C.textSoft, marginBottom: '2px' }}>{label}</div>
        <div style={{ fontSize: '0.738rem', fontWeight: 600, color: C.text, lineHeight: 1.45, wordBreak: breakWord ? 'break-word' : 'normal' }}>{value}</div>
      </div>
    </div>
  );

  return (
    <div style={{ ...wrap }}>
      {/* ── Header card (light) ── */}
      <div style={{
        background: 'white', border: `1px solid ${C.line}`, borderRadius: '16px', padding: '14px 16px', marginBottom: '12px',
        display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap',
      }}>
        <div style={{ width: '40px', height: '40px', borderRadius: '12px', background: C.tint, color: C.navy, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <i className="pi pi-building-columns" style={{ fontSize: '1.05rem' }}></i>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 style={{ margin: '0 0 3px 0', fontSize: '0.99rem', fontWeight: 800, letterSpacing: '-0.3px', color: C.text }}>{institute.name}</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.675rem', color: C.textSoft }}>
            <i className="pi pi-calendar" style={{ fontSize: '0.675rem', color: C.navy }}></i>
            Established {formatDate(institute.created_at)}
          </div>
        </div>

        {/* Institute ID */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
          <span style={{ fontSize: '0.65rem', fontWeight: 600, color: C.textSoft }}>Institute ID</span>
          <code style={{ padding: '4px 9px', background: C.tintSoft, border: `1px solid ${C.line}`, borderRadius: '8px', fontSize: '0.675rem', color: C.navy, fontWeight: 600, fontFamily: "'JetBrains Mono', monospace" }}>
            {institute.institute_id}
          </code>
          <button
            onClick={copyId}
            aria-label="Copy institute ID"
            style={{
              width: '26px', height: '26px', borderRadius: '8px', border: `1px solid ${C.lineStrong}`,
              background: copied ? C.navy : 'white', color: copied ? C.gold : C.navy, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s ease',
            }}
          >
            <i className={`pi ${copied ? 'pi-check' : 'pi-copy'}`} style={{ fontSize: '0.7rem' }}></i>
          </button>
        </div>
      </div>

      {/* ── Details ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '12px' }}>
        <InfoTile icon="pi-envelope" label="Email" value={institute.email} breakWord />
        <InfoTile icon="pi-phone" label="Phone" value={institute.contact_no} />
        <InfoTile icon="pi-map-marker" label="Location" value={institute.address} />
      </div>
    </div>
  );
};

export default InstituteProfile;